import React, { useState, useEffect } from 'react';
import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaArrowUp } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useLanguageContext } from './homenav';

const Footer = () => {
  const year = new Date().getFullYear();
  const [showTopBtn, setShowTopBtn] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const circumference = 2 * Math.PI * 20; // 2 * PI * radius

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      const scrollPercent = scrollTop / (docHeight - clientHeight);
      
      setScrollProgress(scrollPercent);
      setShowTopBtn(scrollTop > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const { t } = useLanguageContext();

  return (
    <footer className="bg-gray-900 text-gray-300 relative">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {/* Column 1: About */}
          <div className="md:col-span-2 lg:col-span-1 flex items-start text-left md:-translate-x-14 lg:-translate-x-24">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 shrink-0">
                <img src="/aaapdmainlogo.jpg" alt="AAAPD Logo" className="h-20 w-auto" />
              </Link>
              <p className="text-gray-400">{t('footerAboutBlurb')}</p>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 uppercase tracking-wider">{t('quickLinks')}</h3>
            <div className="flex">
              <ul className="w-1/2 space-y-1 sm:space-y-2 pl-0 m-0 list-none pr-0 text-sm md:text-base">
                <li><Link to="/whoweare" className="hover:text-sky-400 transition-colors">{t('whoWeAre')}</Link></li>
                <li><Link to="/programs" className="hover:text-sky-400 transition-colors">{t('programs')}</Link></li>
                <li><Link to="/memberss" className="hover:text-sky-400 transition-colors">{t('Partnerss')}</Link></li>
                <li><Link to="/gallery" className="hover:text-sky-400 transition-colors">{t('gallery')}</Link></li>
              </ul>
              <ul className="w-1/2 space-y-1 sm:space-y-2 pl-0 m-0 list-none -ml-6 sm:-ml-8 md:-ml-10 text-sm md:text-base">
                <li><Link to="/contactus" className="hover:text-sky-400 transition-colors">{t('contactUs')}</Link></li>
                <li><Link to="/login" className="hover:text-sky-400 transition-colors">{t('login')}</Link></li>
                <li><Link to="/donate" className="hover:text-sky-400 transition-colors">{t('donate')}</Link></li>
              </ul>
            </div>
          </div>

          {/* Column 3: Funded By */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 uppercase tracking-wider">{t('fundedBy')}</h3>
            <div className="p-4 rounded-lg bg-white">
              <div className="flex flex-col items-center gap-4">
                <a
                  href="https://www.zoa-international.com/ethiopia"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="ZOA International"
                  className="shrink-0"
                >
                  <img src="/zoalogo.png" alt="ZOA Logo" className="h-12 w-auto object-contain" />
                </a>
                <div className="h-px w-full max-w-xs bg-gray-200" />
                <a
                  href="https://weareable.ngo/en/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="ZOA International"
                  className="shrink-0"
                >
                  <img src="/we-are-able.png" alt="We are Able Logo" className="h-12 w-auto object-contain" />
                </a>
                <div className="text-gray-800 text-center">
                  <p className="font-semibold">{t('zoaTagline')}</p>
                  <p className="font-semibold">{t('WaATagline')}</p>
                  <p className="text-sm text-gray-600 mt-1">{t('zoaFundedNote')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 text-sm mb-4 md:mb-0">
            &copy; {year} AAAPD. {t('rightsReserved')}
          </p>
          <div className="flex space-x-4">
            <a href="#" className="text-gray-500 hover:text-sky-400 transition-colors"><FaFacebookF /></a>
            <a href="#" className="text-gray-500 hover:text-sky-400 transition-colors"><FaTwitter /></a>
            <a href="#" className="text-gray-500 hover:text-sky-400 transition-colors"><FaInstagram /></a>
            <a href="#" className="text-gray-500 hover:text-sky-400 transition-colors"><FaLinkedinIn /></a>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {showTopBtn && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-14 h-14 rounded-full shadow-lg bg-white/80 backdrop-blur-sm text-brown-700 hover:bg-white transition-all duration-300 z-50 flex items-center justify-center"
          aria-label="Back to top"
        >
          <svg className="w-14 h-14 transform -rotate-90" viewBox="0 0 44 44">
            <circle
              className="text-gray-300"
              strokeWidth="4"
              stroke="currentColor"
              fill="transparent"
              r="20"
              cx="22"
              cy="22"
            />
            <circle
              className="text-brown-600"
              strokeWidth="4"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - scrollProgress)}
              strokeLinecap="round"
              stroke="currentColor"
              fill="transparent"
              r="20"
              cx="22"
              cy="22"
            />
          </svg>
          <FaArrowUp size={20} className="absolute text-[#6b4f3b]" />
        </button>
      )}
    </footer>
  );
};

export default Footer;
